/* Slang translation for search.
 *
 * The problem: a customer searched "window" and got "Nothing came back". In grillz
 * language a window IS an open face — the hollow cutout that shows the natural tooth —
 * but no product carries that word, so Shopify returns nothing and the visitor leaves
 * thinking we do not make it.
 *
 * Shopify's synonyms live inside the Search & Discovery app and have no Admin API, so
 * this cannot be configured from code. The theme translates instead.
 *
 * Only fires when a search returned ZERO results, so it can never override a search
 * that already worked. The destination carries ?from= so the page can say what it did.
 *
 * Sources: references/grillz-customer-vocabulary + real internal search terms pulled
 * from GA4 (customers spelled "fang" six different ways).
 */
(function () {
  'use strict';

  var MAP = {
    /* open face — the one that started this */
    'window': 'open face', 'windows': 'open face', 'open window': 'open face',
    'open windows': 'open face', 'open cut': 'open face', 'open cuts': 'open face',
    'cutout': 'open face', 'cut out': 'open face', 'cutouts': 'open face',
    'hollow': 'open face', 'see through': 'open face', 'openface': 'open face',
    'open faces': 'open face',

    /* iced */
    'bus down': 'iced', 'bussdown': 'iced', 'bust down': 'iced', 'busdown': 'iced',
    'blinged': 'iced', 'blinged out': 'iced', 'bling': 'iced', 'blingy': 'iced',
    'frosty': 'iced', 'icy': 'iced', 'ice out': 'iced', 'iced out': 'iced',
    'flooded': 'iced', 'drip': 'iced', 'drippy': 'iced', 'froze': 'iced', 'frozen': 'iced',
    'vvs': 'diamond', 'vs': 'diamond', 'rocks': 'diamond', 'ice': 'diamond',
    'pave': 'diamond', 'paved': 'diamond', 'moiss': 'moissanite',
    'cz': 'colored stones', 'lab': 'diamond', 'lab grown': 'diamond',

    /* solid */
    'closed face': 'solid', 'closed': 'solid', 'covers': 'solid', 'cover': 'solid',
    'plain': 'solid', 'solid cap': 'solid', 'solid caps': 'solid', 'solids': 'solid',

    /* fangs */
    'vampire': 'fang', 'vamp': 'fang', 'vampire teeth': 'fang', 'vampire fangs': 'fang',
    'pointed': 'fang', 'points': 'fang', 'canine': 'fang', 'canines': 'fang',
    'cuspid': 'fang', 'cuspids': 'fang', 'k9': 'fang', 'fangs': 'fang',
    'dracula': 'fang', 'blood fang': 'fang',

    /* gaps */
    'gap tooth': 'gap filler', 'gap teeth': 'gap filler', 'diastema': 'gap filler',
    'gap': 'gap filler', 'gaps': 'gap filler', 'filler': 'gap filler',

    /* texture and cut */
    'nugget': 'nuggets', 'lumps': 'nuggets', 'chunky': 'nuggets', 'bumpy': 'nuggets',
    'dc': 'diamond cut', 'faceted': 'diamond cut', 'engraved': 'diamond cut',
    'laser cut': 'diamond cut', 'shiny cut': 'diamond cut',
    'honey comb': 'honeycomb', 'hex': 'honeycomb', 'hexagon': 'honeycomb',

    /* build shape */
    'connected': 'bridge', 'one piece': 'bridge', 'joined': 'bridge', 'bar': 'bridge',
    'one tooth': 'single tooth', 'single cap': 'single tooth', '1 tooth': 'single tooth',
    'single': 'single tooth', 'one cap': 'single tooth',
    'full mouth': 'full set', 'all teeth': 'full set', 'top and bottom': 'full set',
    'fronts': 'top', 'front teeth': 'top', 'uppers': 'top', 'upper': 'top',
    'lowers': 'bottom', 'lower': 'bottom', 'bottoms': 'bottom',

    /* colour and finish */
    'color drop': 'enamel', 'color drops': 'enamel', 'color fill': 'enamel',
    'colour': 'enamel', 'painted': 'enamel', 'color': 'enamel', 'colored': 'enamel',
    'black': 'black diamond', 'all black': 'black diamond', 'blackout': 'black diamond',
    'black rhodium': 'black diamond',

    /* metals */
    'yella': 'gold', 'yellow': 'gold', 'yellow gold': 'gold', 'yg': 'gold',
    'wg': 'white gold', 'rg': 'rose gold', 'rose': 'rose gold', 'rosey': 'rose gold',
    'pink gold': 'rose gold', '925': 'silver', 'sterling': 'silver', 'plat': 'platinum',

    /* intent */
    'perms': 'permanent', 'bonded': 'permanent', 'glued': 'permanent',
    'slip on': 'removable', 'pop in': 'removable'
  };

  function normalise(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function translate(term) {
    var t = normalise(term);
    if (!t) return null;
    if (MAP[t]) return MAP[t];
    // strip a trailing "grillz"/"grill"/"teeth" and retry: "window grillz" -> "window"
    var stripped = t.replace(/\s*(grillz|grills|grill|teeth|tooth|cap|caps)$/, '').trim();
    if (stripped && stripped !== t && MAP[stripped]) return MAP[stripped];
    return null;
  }

  /* On the destination page: say what we did, so nobody wonders why the words changed. */
  var params = new URLSearchParams(window.location.search);
  var from = params.get('from');
  if (from) {
    var count = document.querySelector('[data-rescount]');
    if (count) {
      var note = document.createElement('em');
      note.style.opacity = '.7';
      note.textContent = ' · searched instead of "' + from + '"';
      count.appendChild(note);
    }
  }

  var root = document.querySelector('[data-slang-rescue]');
  if (!root) return;

  var term = root.getAttribute('data-term') || '';
  var alt = translate(term);
  if (!alt) return;

  var url = root.getAttribute('data-search-url') || '/search';
  var target = url + '?q=' + encodeURIComponent(alt) + '&type=product&from=' + encodeURIComponent(term);

  // Announce it, then go. replace() so Back returns to where they came from rather than
  // bouncing them through the empty result page again.
  root.innerHTML = '<p>No exact match for &ldquo;' + term.replace(/[<>&]/g, '') +
    '&rdquo;. In grillz talk that usually means <strong>' + alt + '</strong> — taking you there&hellip;</p>';
  window.setTimeout(function () { window.location.replace(target); }, 900);
})();
